import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { unstable_useBlocker as useBlocker } from 'react-router-dom'

import { F0ActionBar, F0ActionBarRef } from '@factorialco/f0-react/dist/experimental'
import { ActionBarStatus } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { QuestionType, QuestionnaireType, SectionType } from './types'

const SINGLE_CHOICE_TYPES: ReadonlyArray<QuestionType['type']> = ['select', 'dropdownsingle']
const MULTI_CHOICE_TYPES: ReadonlyArray<QuestionType['type']> = ['multiselect', 'dropdownmulti']

type ValidationResult = { ok: true } | { ok: false; message: string }

const isSection = (element: QuestionnaireType['elements'][number]): element is SectionType =>
  element.type === 'section'

/**
 * Validate the questionnaire before save. Returns the first user-facing error
 * found, walking sections and their questions in display order. Rules:
 *   - sections must have a title
 *   - questions must have a title
 *   - single-/multi-choice questions must have at least one option
 *   - single-choice questions must have at most one option marked correct
 *   - Knowledge Test only: single- and multi-choice questions must have at
 *     least one option marked correct (so the test is gradable)
 */
function validateQuestionnaire(
  questionnaire: QuestionnaireType,
  isKnowledgeTest: boolean
): ValidationResult {
  let questionIndex = 0
  let sectionIndex = 0

  for (const element of questionnaire.elements) {
    if (!element) continue

    if (isSection(element)) {
      sectionIndex += 1
      if (!element.label.trim()) {
        return {
          ok: false,
          message: i18n.t('forms.action_bar.validation.section_missing_title', {
            position: sectionIndex,
          }),
        }
      }
      for (const question of element.elements) {
        questionIndex += 1
        const error = validateQuestion(question, questionIndex, isKnowledgeTest)
        if (error) return { ok: false, message: error }
      }
    } else {
      questionIndex += 1
      const error = validateQuestion(element, questionIndex, isKnowledgeTest)
      if (error) return { ok: false, message: error }
    }
  }

  return { ok: true }
}

function validateQuestion(
  question: QuestionType,
  position: number,
  isKnowledgeTest: boolean
): string | null {
  if (!question.label.trim()) {
    return i18n.t('forms.action_bar.validation.question_missing_title', { position })
  }

  const isSingleChoice = SINGLE_CHOICE_TYPES.includes(question.type)
  const isMultiChoice = MULTI_CHOICE_TYPES.includes(question.type)

  if (isSingleChoice || isMultiChoice) {
    const options = question.options ?? []
    if (options.length === 0) {
      return i18n.t('forms.action_bar.validation.question_no_options', {
        title: question.label.trim(),
      })
    }

    const correctCount = options.filter((o) => o.isCorrect).length

    if (isSingleChoice && correctCount > 1) {
      return i18n.t('forms.action_bar.validation.single_choice_multiple_correct', {
        title: question.label.trim(),
      })
    }

    if (isKnowledgeTest && correctCount === 0) {
      return i18n.t('forms.action_bar.validation.question_missing_correct_answer', {
        title: question.label.trim(),
      })
    }
  }

  return null
}

interface ActionBarState {
  label: string
  status?: ActionBarStatus
}

function getDefaultState(): ActionBarState {
  return { label: i18n.t('forms.action_bar.label') }
}

interface FormEditorActionsProps {
  isDirty: boolean
  questionnaire: QuestionnaireType
  isKnowledgeTest: boolean
  onSave: () => Promise<boolean>
  onDiscard: () => void
  isThinking?: boolean
}

export const FormEditorActions = ({
  isDirty,
  questionnaire,
  isKnowledgeTest,
  onSave,
  onDiscard,
  isThinking,
}: FormEditorActionsProps) => {
  const actionBarRef = useRef<F0ActionBarRef>(null)
  const [barState, setBarState] = useState<ActionBarState>(getDefaultState)

  const blocker = useBlocker(isDirty)

  const validation = useMemo(
    () => validateQuestionnaire(questionnaire, isKnowledgeTest),
    [questionnaire, isKnowledgeTest]
  )

  useEffect(() => {
    if (barState.status === 'error' && validation.ok) {
      setBarState(getDefaultState())
    }
  }, [validation, barState.status])

  const handleSave = useCallback(async () => {
    if (!validation.ok) {
      setBarState({ label: validation.message, status: 'error' })
      actionBarRef.current?.wiggle({ errorHighlight: true })
      return
    }
    setBarState({ label: i18n.t('forms.action_bar.label'), status: 'loading' })
    const success = await onSave()
    if (success) {
      setBarState({ label: i18n.t('forms.action_bar.saved'), status: 'success' })
      setTimeout(() => setBarState(getDefaultState()), 1500)
    } else {
      setBarState(getDefaultState())
    }
  }, [validation, onSave])

  const handleDiscard = useCallback(() => {
    setBarState(getDefaultState())
    onDiscard()
  }, [onDiscard])

  useEffect(() => {
    if (blocker.state === 'blocked') {
      actionBarRef.current?.wiggle({ errorHighlight: true })
      blocker.reset?.()
    }
  }, [blocker])

  return (
    <F0ActionBar
      ref={actionBarRef}
      isOpen={isDirty || barState.status === 'success'}
      variant="light"
      status={barState.status}
      primaryActions={[
        { label: i18n.t('forms.action_bar.save'), onClick: handleSave, disabled: isThinking },
      ]}
      secondaryActions={[
        { label: i18n.t('forms.action_bar.discard'), onClick: handleDiscard, disabled: isThinking },
      ]}
      label={barState.label}
    />
  )
}
